import { danglingReferences, type SceneDocument, topologicalOrder } from '@/domain/model';

import { type DecodedDocument, decode, DecodeError } from './codec';
import { migrate } from './migrations';
import { FORMAT_TAG } from './schema/v1';

/**
 * The complete load path: migrate, decode, then check the result is well-founded.
 *
 * Splitting migration from decoding keeps each honest — migrations reshape raw JSON and
 * know nothing about the domain, the codec builds domain objects and knows nothing about
 * old versions.
 */

export interface LoadResult extends DecodedDocument {
  /** Migrations applied on the way in, for the "this file was upgraded" notice. */
  readonly migrations: readonly string[];
}

/**
 * Loads a parsed JSON value.
 *
 * Structural problems that would produce silently broken geometry are caught here rather
 * than surfacing much later:
 *
 * - A **dangling parent** makes an entity that can never be evaluated and simply never
 *   appears. The entity is dropped with a warning naming what it referenced.
 * - A **cycle** makes the document unorderable. It cannot come from the editor, which
 *   rejects cycles at construction time, so a file containing one was hand-edited.
 */
export function loadDocument(raw: unknown): LoadResult {
  // The format tag is checked before the version, because the overwhelmingly common
  // failure is a user picking the wrong file. "Not an EduSketch file" is an answer;
  // "missing or invalid schemaVersion" is a puzzle.
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new DecodeError('expected an object', '');
  }
  const tag = (raw as Record<string, unknown>)['format'];
  if (tag !== FORMAT_TAG) {
    throw new DecodeError(`not an EduSketch file (format is ${String(tag)})`, '');
  }

  const migrated = migrate(raw);
  const decoded = decode(migrated.document);

  const warnings = [...decoded.warnings];
  let document = decoded.document;

  const dangling = danglingReferences(document);
  if (dangling.length > 0) {
    const missing = new Set(dangling);
    const entities = new Map(document.entities);
    for (const [id, entity] of document.entities) {
      const broken = referencesMissing(entity, missing);
      if (broken.length === 0) continue;
      entities.delete(id);
      warnings.push(`dropped entity "${id}": references missing ${broken.join(', ')}`);
    }
    document = { ...document, entities };
  }

  if (topologicalOrder(document) === null) {
    throw new DecodeError('the file contains a circular construction and cannot be evaluated', '');
  }

  return { document, unknown: decoded.unknown, warnings, migrations: migrated.applied };
}

export function loadDocumentFromJson(text: string): LoadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new DecodeError(
      `not valid JSON: ${error instanceof Error ? error.message : String(error)}`,
      ''
    );
  }
  return loadDocument(parsed);
}

function referencesMissing(
  entity: SceneDocument['entities'] extends ReadonlyMap<unknown, infer E> ? E : never,
  missing: ReadonlySet<string>
): readonly string[] {
  const broken: string[] = [];
  for (const value of Object.values(entity.source)) {
    if (typeof value === 'string' && missing.has(value)) broken.push(value);
    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === 'string' && missing.has(item)) broken.push(item);
      }
    }
  }
  return broken;
}
